/**
 * 업무 프로젝트 자동화 — 노드 캔버스 부품(J7c1 · 외부 라이브러리 없음).
 *
 * - 잔디 실측 위치·크기: 왼쪽 팔레트(폭 200 · 항목 183×36) · 가운데 캔버스(점 무늬 · 왼쪽 아래 세로 버튼
 *   확대·축소·전체 맞춤 · 미니맵 없음) · 연결선은 곡선 · 트리거는 하나만 · 노드에 삭제 버튼이 없다(선택 후
 *   Delete·Backspace). 색·모양은 uiStyles.
 * - 그래프는 부모가 든다(controlled) — 이 부품은 onChange(새 그래프)로만 알린다. 끄는 중 위치·임시 선·
 *   선택된 연결·확대 비율·화면 위치만 로컬이다.
 * - 노드 옮기기·선 잇기·화면 옮기기는 document mousemove/mouseup(선례 AppSidebar startResize) — mouseup 에서
 *   리스너를 모두 뗀다. 팔레트 → 캔버스만 HTML5 끌어 놓기(선례 TaskPmKanban)이고, 클릭해도 놓인다.
 * - 🔴 웹 배율(<html> zoom)이면 clientX·rect 는 화면 px, 캔버스 좌표는 CSS px ÷ zoom 이다 — 놓기·옮기기·잇기·
 *   화면 옮기기는 화면 px 차이를 캔버스 rect 폭 ÷ offsetWidth(rootScaleOf, jsdom 은 1)로 먼저 나눈다.
 *   CLICK_SLOP 판정은 화면 px 그대로다. placeByClick 은 모바일과 같은 길이라 이번에 보정하지 않았다.
 */
import { useEffect, useRef, useState } from 'react';
import { scaleOf } from '../../../lib/floatingBounds';
import { COLORS, RADIUS } from '../../../utils/uiStyles';
import { GROUPS, HANDLE_LABELS, NODE_H, NODE_TYPES, NODE_W, canConnect, nextId, nodeSpec, summarize } from './automationCatalog';

const DRAG_MIME = 'application/x-taskpm-node';
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 1.5;
const CLICK_SLOP = 3;      // 이만큼(px) 이하로 움직였으면 끌기가 아니라 클릭이다
const FIT_PAD = 40;        // 전체 맞춤 때 노드 경계 상자 바깥 여백
const GRID = 20;           // 점 무늬 간격(zoom 1 기준)
const STACK_GAP = 24;      // 클릭으로 놓을 자리가 차 있으면 NODE_H + 이만큼씩 아래로 비킨다
const SVG_SIZE = 10000;    // 연결층 크기(overflow visible 이라 음수 좌표도 그린다)

const GROUP_COLOR = { trigger: COLORS.warn, condition: COLORS.primary, action: COLORS.success };
const PALETTE = Object.keys(NODE_TYPES).map((type) => ({ type, ...NODE_TYPES[type] }));
// 카탈로그에 없는 종류가 와도(서버 쪽이 먼저 늘어난 경우) 그리기는 한다 — 연결점 없이.
const UNKNOWN_SPEC = { group: 'action', input: false, outputs: [] };

const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
const handleRatio = (handle) => (handle === 'yes' ? 0.3 : handle === 'no' ? 0.7 : 0.5);
const edgePath = (sx, sy, tx, ty) => {
  const dx = Math.max(40, Math.abs(tx - sx) / 2);
  return `M ${sx} ${sy} C ${sx + dx} ${sy}, ${tx - dx} ${ty}, ${tx} ${ty}`;
};

// mousemove·mouseup 을 document 에 걸고, mouseup 에서 둘 다 뗀 뒤 onUp 을 부른다.
const track = (onMove, onUp) => {
  const up = (ev) => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', up);
    onUp(ev);
  };
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', up);
};

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const handleBase = {
  position: 'absolute', width: '10px', height: '10px', boxSizing: 'border-box', borderRadius: '50%',
  border: `2px solid ${COLORS.primaryBorder}`, background: COLORS.surface, transform: 'translateY(-50%)',
};
const zoomButton = (index) => ({
  display: 'block', width: '26px', height: '26px', boxSizing: 'border-box', padding: 0,
  borderTop: index === 0 ? `1px solid ${COLORS.border}` : 'none',
  borderRight: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}`, borderLeft: `1px solid ${COLORS.border}`,
  borderRadius: index === 0 ? `${RADIUS.sm} ${RADIUS.sm} 0 0` : index === 2 ? `0 0 ${RADIUS.sm} ${RADIUS.sm}` : 0,
  background: COLORS.surface, color: COLORS.textSub, fontSize: '14px', lineHeight: '24px', cursor: 'pointer',
});

export default function AutomationCanvas({
  graph,
  onChange,
  selectedId,
  onSelect,
  readOnly = false,
  errorNodeId = null,
  summaryOverride = {},
  height = '560px',
}) {
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragPos, setDragPos] = useState(null);        // { id, x, y } — 끄는 중인 노드의 로컬 위치
  const [draft, setDraft] = useState(null);            // { sourceId, handle, x, y } — 잇는 중인 임시 선 끝(캔버스 좌표)
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);

  const nodes = Array.isArray(graph && graph.nodes) ? graph.nodes : [];
  const edges = Array.isArray(graph && graph.edges) ? graph.edges : [];
  const hasTrigger = nodes.some((n) => nodeSpec(n.type)?.group === 'trigger');

  const specOf = (node) => nodeSpec(node.type) || { ...UNKNOWN_SPEC, label: String(node.type) };
  const posOf = (node) => (dragPos && dragPos.id === node.id
    ? dragPos
    : { x: Number(node.x) || 0, y: Number(node.y) || 0 });
  const outPoint = (node, handle) => {
    const p = posOf(node);
    return { x: p.x + NODE_W, y: p.y + NODE_H * handleRatio(handle) };
  };
  const inPoint = (node) => {
    const p = posOf(node);
    return { x: p.x, y: p.y + NODE_H / 2 };
  };

  const emit = (nextNodes, nextEdges) => { if (onChange) onChange({ ...graph, nodes: nextNodes, edges: nextEdges }); };
  const select = (id) => { if (onSelect) onSelect(id); };
  const rectOf = () => (canvasRef.current
    ? canvasRef.current.getBoundingClientRect()
    : { left: 0, top: 0, width: 0, height: 0 });
  // 웹 배율 = 화면 px ÷ CSS px. 🔴 이름이 rs 계열인 것은 `zoom` 이 이미 캔버스 확대 비율이라서다.
  const rootScaleOf = (rect) => scaleOf(rect.width, canvasRef.current ? canvasRef.current.offsetWidth : 0);
  // 🔴 mousedown 에서 preventDefault 하면 브라우저가 포커스를 옮기지 않는다 — Delete 를 받으려면 직접 준다.
  const focusCanvas = () => {
    const el = canvasRef.current;
    if (el && document.activeElement !== el) el.focus({ preventScroll: true });
  };

  // ─── 놓기 ───────────────────────────────────────────────
  const addNode = (type, x, y) => {
    const spec = nodeSpec(type);
    if (readOnly || !spec) return;                        // 모르는 종류는 무시
    if (spec.group === 'trigger' && hasTrigger) return;   // 트리거는 하나만
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const id = nextId(nodes, 'n');
    emit([...nodes, { id, type, x, y, data: spec.data() }], edges);
    setSelectedEdgeId(null);
    select(id);
  };

  // 클릭으로 놓기(키보드·시험용) — 보이는 영역 가운데, 자리가 차 있으면 아래로 비킨다.
  const placeByClick = (type) => {
    const rect = rectOf();
    const x = Math.round((rect.width / 2 - pan.x) / zoom - NODE_W / 2);
    let y = Math.round((rect.height / 2 - pan.y) / zoom - NODE_H / 2);
    const taken = (ty) => nodes.some((n) => {
      const p = posOf(n);
      return Math.abs(p.x - x) < NODE_W && Math.abs(p.y - ty) < NODE_H;
    });
    // 노드 하나가 막는 자리는 많아야 둘이라 nodes.length * 2 번이면 빈자리에 닿는다.
    for (let i = 0; i <= nodes.length * 2 && taken(y); i += 1) y += NODE_H + STACK_GAP;
    addNode(type, x, y);
  };

  const onDragOver = (e) => {
    if (readOnly) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  const onDrop = (e) => {
    e.preventDefault();
    if (readOnly) return;
    const type = e.dataTransfer ? e.dataTransfer.getData(DRAG_MIME) : '';
    const rect = rectOf();
    const rs = rootScaleOf(rect);
    const x = Math.round(((e.clientX - rect.left) / rs - pan.x) / zoom - NODE_W / 2);
    const y = Math.round(((e.clientY - rect.top) / rs - pan.y) / zoom - NODE_H / 2);
    addNode(type, x, y);
  };

  // ─── 노드 옮기기 ────────────────────────────────────────
  const startNodeDrag = (node) => (e) => {
    if (e.button !== 0) return;
    if (e.target.closest && e.target.closest('[data-handle]')) return;   // 연결점은 잇기·받기 전용
    e.preventDefault();
    focusCanvas();
    setSelectedEdgeId(null);
    if (readOnly) { select(node.id); return; }
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = posOf(node);
    const z = zoom;
    const rs = rootScaleOf(rectOf());
    let last = { x: startX, y: startY };
    const at = () => ({ x: origin.x + (last.x - startX) / rs / z, y: origin.y + (last.y - startY) / rs / z });
    track((ev) => {
      last = { x: ev.clientX, y: ev.clientY };
      setDragPos({ id: node.id, ...at() });
    }, () => {
      setDragPos(null);
      // 🔴 mouseup 좌표가 아니라 마지막 mousemove 로 잰다(놓는 순간 좌표가 비는 경우가 있다).
      if (Math.hypot(last.x - startX, last.y - startY) <= CLICK_SLOP) { select(node.id); return; }
      const p = at();
      const x = Math.round(p.x);
      const y = Math.round(p.y);
      emit(nodes.map((n) => (n.id === node.id ? { ...n, x, y } : n)), edges);
    });
  };

  // ─── 선 잇기 ────────────────────────────────────────────
  const startConnect = (node, handle) => (e) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    e.preventDefault();
    focusCanvas();
    setSelectedEdgeId(null);
    if (readOnly) { select(node.id); return; }
    const rect = rectOf();
    const rs = rootScaleOf(rect);
    const z = zoom;
    const origin = pan;
    const start = outPoint(node, handle);
    setDraft({ sourceId: node.id, handle, x: start.x, y: start.y });
    track((ev) => {
      const x = ((ev.clientX - rect.left) / rs - origin.x) / z;
      const y = ((ev.clientY - rect.top) / rs - origin.y) / z;
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      setDraft((d) => (d ? { ...d, x, y } : d));
    }, (ev) => {
      setDraft(null);
      const target = ev.target && ev.target.closest ? ev.target.closest('[data-handle="in"]') : null;
      const host = target ? target.closest('[data-node-id]') : null;
      if (!host) return;
      const targetId = host.getAttribute('data-node-id');
      if (!canConnect({ nodes, edges }, node.id, handle, targetId)) return;
      emit(nodes, [...edges, { id: nextId(edges, 'e'), source: node.id, handle, target: targetId }]);
    });
  };

  // ─── 화면 옮기기(빈 캔버스) ─────────────────────────────
  const startPan = (e) => {
    if (e.button !== 0) return;
    const t = e.target;
    if (t && t.closest && (t.closest('[data-node-id]') || t.closest('[data-edge-id]'))) return;
    e.preventDefault();
    focusCanvas();
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = pan;
    const rs = rootScaleOf(rectOf());
    let last = { x: startX, y: startY };
    track((ev) => {
      last = { x: ev.clientX, y: ev.clientY };
      setPan({ x: origin.x + (last.x - startX) / rs, y: origin.y + (last.y - startY) / rs });
    }, () => {
      if (Math.hypot(last.x - startX, last.y - startY) <= CLICK_SLOP) {
        setSelectedEdgeId(null);
        select(null);
      }
    });
  };

  const clickEdge = (edgeId) => (e) => {
    e.stopPropagation();
    focusCanvas();
    setSelectedEdgeId(edgeId);
    select(null);
  };

  // ─── 지우기 ─────────────────────────────────────────────
  const onKeyDown = (e) => {
    if (e.key !== 'Delete' && e.key !== 'Backspace') return;
    const t = e.target;
    const tag = t && t.tagName ? String(t.tagName).toUpperCase() : '';
    // 🔴 입력칸에서 온 키는 글 지우기다(J7d 패널) — 노드를 지우지 않는다.
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
    if (readOnly) return;
    if (selectedEdgeId && edges.some((ed) => ed.id === selectedEdgeId)) {
      e.preventDefault();
      emit(nodes, edges.filter((ed) => ed.id !== selectedEdgeId));
      setSelectedEdgeId(null);
      select(null);
      return;
    }
    if (selectedId != null && nodes.some((n) => n.id === selectedId)) {
      e.preventDefault();
      emit(
        nodes.filter((n) => n.id !== selectedId),
        edges.filter((ed) => ed.source !== selectedId && ed.target !== selectedId),
      );
      select(null);
    }
  };

  // ─── 확대·축소·전체 맞춤 ────────────────────────────────
  const zoomBy = (delta) => setZoom((z) => clampZoom(Math.round((z + delta) * 10) / 10));
  const fitView = () => {
    if (!nodes.length) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
      return;
    }
    const rect = rectOf();
    if (!(rect.width > 0 && rect.height > 0)) return;    // 아직 크기가 없으면(숨김 등) 맞출 수 없다
    const xs = nodes.map((n) => posOf(n).x);
    const ys = nodes.map((n) => posOf(n).y);
    const minX = Math.min(...xs) - FIT_PAD;
    const minY = Math.min(...ys) - FIT_PAD;
    const w = Math.max(...xs) + NODE_W + FIT_PAD - minX;
    const h = Math.max(...ys) + NODE_H + FIT_PAD - minY;
    const z = clampZoom(Math.min(rect.width / w, rect.height / h));
    setZoom(z);
    setPan({ x: (rect.width - w * z) / 2 - minX * z, y: (rect.height - h * z) / 2 - minY * z });
  };
  // 🔴 마운트 때 한 번만(잔디는 열 때 전체 맞춤) — 뒤에 노드가 생겨도 다시 맞추지 않는다.
  useEffect(() => {
    if (nodes.length) fitView();
  }, []);

  // ─── 그리기 ─────────────────────────────────────────────
  const renderPaletteItem = (item) => {
    const triggerBlocked = item.group === 'trigger' && hasTrigger;
    const disabled = readOnly || triggerBlocked;
    const side = `1px solid ${COLORS.border}`;
    return (
      <button key={item.type} type="button" disabled={disabled} aria-disabled={disabled ? 'true' : undefined}
        draggable={!disabled} title={triggerBlocked ? '트리거는 하나만 놓을 수 있습니다' : undefined}
        onDragStart={(e) => {
          if (disabled) { e.preventDefault(); return; }
          e.dataTransfer.setData(DRAG_MIME, item.type);
          e.dataTransfer.effectAllowed = 'copy';
        }}
        onClick={() => placeByClick(item.type)}
        style={{
          ...ellipsis, display: 'block', width: '183px', height: '36px', flexShrink: 0, boxSizing: 'border-box',
          padding: '0 10px', textAlign: 'left', fontSize: '13px',
          color: disabled ? COLORS.textMuted : COLORS.text, background: disabled ? COLORS.bg : COLORS.surface,
          borderTop: side, borderRight: side, borderBottom: side, borderLeft: `3px solid ${GROUP_COLOR[item.group]}`,
          borderRadius: RADIUS.sm, cursor: disabled ? 'not-allowed' : 'grab',
        }}>
        {item.label}
      </button>
    );
  };

  const renderNode = (node) => {
    const spec = specOf(node);
    const pos = posOf(node);
    const selected = selectedId != null && node.id === selectedId;
    const error = errorNodeId != null && node.id === errorNodeId;
    const side = `${error || selected ? 2 : 1}px solid ${error ? COLORS.danger : selected ? COLORS.primary : COLORS.border}`;
    const override = summaryOverride ? summaryOverride[node.id] : undefined;
    return (
      <div key={node.id} role="button" aria-label={`${spec.label} 노드`} aria-pressed={selected} data-node-id={node.id}
        onMouseDown={startNodeDrag(node)}
        style={{
          position: 'absolute', left: pos.x, top: pos.y, width: NODE_W, height: NODE_H, boxSizing: 'border-box',
          padding: '8px 10px', background: error ? COLORS.dangerSoft : COLORS.surface,
          borderTop: side, borderRight: side, borderBottom: side, borderLeft: `3px solid ${GROUP_COLOR[spec.group] || COLORS.border}`,
          borderRadius: RADIUS.md, cursor: readOnly ? 'pointer' : 'grab', userSelect: 'none',
        }}>
        {/* 두 줄 17+15 = 32 — 선택 테두리 2px 에서도 안쪽 높이(52−16−4)에 맞는다. */}
        <div style={{ ...ellipsis, fontSize: '13px', fontWeight: 600, lineHeight: '17px', color: COLORS.text }}>{spec.label}</div>
        <div style={{ ...ellipsis, fontSize: '12px', lineHeight: '15px', color: COLORS.textSub }}>{override ?? summarize(node)}</div>
        {spec.input && <span data-handle="in" aria-hidden="true" style={{ ...handleBase, left: '-6px', top: '50%' }} />}
        {spec.outputs.map((handle) => (
          <span key={handle || 'out'} data-handle={handle || 'out'} aria-hidden="true" onMouseDown={startConnect(node, handle)}
            style={{ ...handleBase, right: '-6px', top: `${handleRatio(handle) * 100}%`, cursor: readOnly ? 'default' : 'crosshair' }} />
        ))}
        {spec.outputs.filter(Boolean).map((handle) => (
          <span key={`label-${handle}`}
            style={{
              position: 'absolute', left: '100%', marginLeft: '10px', top: `${handleRatio(handle) * 100}%`,
              transform: 'translateY(-50%)', fontSize: '11px', color: COLORS.textSub, whiteSpace: 'nowrap', pointerEvents: 'none',
            }}>
            {HANDLE_LABELS[handle]}
          </span>
        ))}
      </div>
    );
  };

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const draftSource = draft ? nodeById.get(draft.sourceId) : null;
  const draftStart = draftSource ? outPoint(draftSource, draft.handle) : null;

  return (
    <div style={{
      display: 'flex', height, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md,
      overflow: 'hidden', background: COLORS.surface,
    }}>
      <aside aria-label="노드 목록" style={{
        width: '200px', flex: '0 0 200px', boxSizing: 'border-box', padding: '8px', overflowY: 'auto',
        borderRight: `1px solid ${COLORS.borderSoft}`,
      }}>
        {GROUPS.map((group, index) => (
          <div key={group.value} style={{ marginTop: index ? '12px' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: COLORS.textSub }}>{group.label}</span>
              {group.hint && <span style={{ fontSize: '11px', color: COLORS.textMuted }}>{group.hint}</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {PALETTE.filter((item) => item.group === group.value).map(renderPaletteItem)}
            </div>
          </div>
        ))}
      </aside>

      <div ref={canvasRef} role="application" aria-label="자동화 그림" tabIndex={0}
        onMouseDown={startPan} onDragOver={onDragOver} onDrop={onDrop} onKeyDown={onKeyDown}
        style={{
          flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden', backgroundColor: COLORS.bg,
          backgroundImage: `radial-gradient(${COLORS.border} 1px, transparent 1px)`,
          backgroundSize: `${GRID * zoom}px ${GRID * zoom}px`,
          backgroundPosition: `${pan.x}px ${pan.y}px`,
        }}>
        <div data-testid="automation-viewport" style={{
          position: 'absolute', left: 0, top: 0,
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0',
        }}>
          <svg aria-hidden="true" width={SVG_SIZE} height={SVG_SIZE}
            style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' }}>
            {edges.map((edge) => {
              const source = nodeById.get(edge.source);
              const target = nodeById.get(edge.target);
              if (!source || !target) return null;
              const a = outPoint(source, edge.handle ?? null);
              const b = inPoint(target);
              const d = edgePath(a.x, a.y, b.x, b.y);
              const on = edge.id === selectedEdgeId;
              return (
                <g key={edge.id} data-edge-id={edge.id} onClick={clickEdge(edge.id)} style={{ cursor: 'pointer' }}>
                  {/* 클릭 영역용 투명 겹선 — 1.5px 선은 누르기 어렵다. */}
                  <path d={d} fill="none" stroke="transparent" strokeWidth={12} style={{ pointerEvents: 'stroke' }} />
                  <path d={d} fill="none" stroke={on ? COLORS.primary : COLORS.textMuted} strokeWidth={on ? 2.5 : 1.5}
                    style={{ pointerEvents: 'none' }} />
                </g>
              );
            })}
            {draft && draftStart && (
              <path data-testid="automation-draft-edge" d={edgePath(draftStart.x, draftStart.y, draft.x, draft.y)}
                fill="none" stroke={COLORS.primary} strokeWidth={1.5} strokeDasharray="4 4" style={{ pointerEvents: 'none' }} />
            )}
          </svg>
          {nodes.map(renderNode)}
        </div>

        {/* 왼쪽 아래 세로 버튼 3개 — 누를 때 «화면 옮기기» 가 시작되지 않게 mousedown 을 막는다. */}
        <div onMouseDown={(e) => e.stopPropagation()}
          style={{ position: 'absolute', left: '12px', bottom: '12px', width: '26px', height: '78px' }}>
          <button type="button" aria-label="확대" onClick={() => zoomBy(0.1)} style={zoomButton(0)}>+</button>
          <button type="button" aria-label="축소" onClick={() => zoomBy(-0.1)} style={zoomButton(1)}>−</button>
          <button type="button" aria-label="전체 맞춤" onClick={fitView} style={zoomButton(2)}>⤢</button>
        </div>
      </div>
    </div>
  );
}
